import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
    Calendar, Download, FileSpreadsheet, FileText, Euro, Banknote, 
    Users, TrendingUp, BarChart3, Wine, CalendarDays, CalendarRange,
    PieChart, ArrowUpRight, ArrowDownRight, Minus, UserCheck, ChevronDown
} from 'lucide-react';
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    BarChart, Bar, Cell, PieChart as RPieChart, Pie, LineChart, Line, ComposedChart,
} from 'recharts';
import { ManualReportModal } from './ManualReportModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BAR_NAMES = {
    'lobby': 'Lobby Bar',
    'servis': 'Servis Bar',
    'vitamin': 'Vitamin Bar',
    'havuz': 'Havuz Bar',
    'plaj': 'Plaj Bar',
    'amfi': 'Amfi Bar',
    'teras': 'Teras Bar',
    'alacarte': 'Alacarte Bar',
    'minibar': 'Minibar',
};

const BAR_COLORS = {
    'lobby': '#f59e0b',
    'servis': '#3b82f6',
    'vitamin': '#22c55e',
    'havuz': '#06b6d4',
    'plaj': '#f97316',
    'amfi': '#8b5cf6',
    'teras': '#64748b',
    'alacarte': '#e11d48',
    'minibar': '#10b981',
};

export function Reports() {
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [guestCount, setGuestCount] = useState('');
    const [reportData, setReportData] = useState(null);
    const [trendData, setTrendData] = useState(null);
    const [categoryData, setCategoryData] = useState(null);
    const [trendPeriod, setTrendPeriod] = useState('weekly');
    const [categoryPeriod, setCategoryPeriod] = useState('daily');
    const [trendLoading, setTrendLoading] = useState(false);
    const [categoryLoading, setCategoryLoading] = useState(false);
    const [comparisonData, setComparisonData] = useState(null);
    const [topProductsData, setTopProductsData] = useState(null);
    const [topProductsPeriod, setTopProductsPeriod] = useState('weekly');
    const [guestTrendData, setGuestTrendData] = useState(null);
    const [guestTrendPeriod, setGuestTrendPeriod] = useState('monthly');
    const [guestTrendEndDate, setGuestTrendEndDate] = useState(''); // '' = bugün
    const [guestTrendLoading, setGuestTrendLoading] = useState(false);
    const [selectedBars, setSelectedBars] = useState(new Set(Object.keys(BAR_NAMES)));
    const [selectedColumns, setSelectedColumns] = useState(new Set([
        'product_name', 'unit', 'yesterday_remaining', 'received', 'today_count',
        'consumption', 'unit_price_tl', 'total_cost_tl', 'total_cost_eur'
    ]));
    const [barDropdownOpen, setBarDropdownOpen] = useState(false);
    const [colDropdownOpen, setColDropdownOpen] = useState(false);

    const COLUMN_OPTIONS = [
        { key: 'yesterday_remaining', label: 'Dunden Kalan' },
        { key: 'received', label: 'Bugun Cekilen' },
        { key: 'today_count', label: 'Bugun Sayim' },
        { key: 'consumption', label: 'Tuketim' },
        { key: 'unit_price_tl', label: 'Birim Fiyat (TL)' },
        { key: 'total_cost_tl', label: 'Toplam (TL)' },
        { key: 'total_cost_eur', label: 'Toplam (EUR)' },
    ];

    const toggleBar = (barId) => {
        setSelectedBars(prev => {
            const next = new Set(prev);
            if (next.has(barId)) next.delete(barId);
            else next.add(barId);
            return next;
        });
    };

    const toggleAllBars = () => {
        if (selectedBars.size === Object.keys(BAR_NAMES).length) {
            setSelectedBars(new Set());
        } else {
            setSelectedBars(new Set(Object.keys(BAR_NAMES)));
        }
    };

    const toggleColumn = (key) => {
        setSelectedColumns(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
    const [pdfDownloadUrl, setPdfDownloadUrl] = useState(null);
    const [pdfFilename, setPdfFilename] = useState('rapor.pdf');

    const [downloadLink, setDownloadLink] = useState(null);

    const handleDetailedDownload = async (format) => {
        if (selectedBars.size === 0) {
            toast.error('En az bir bar secin');
            return;
        }
        try {
            toast.info('Hazırlanıyor...');
            const token = localStorage.getItem('token');
            const cols = Array.from(new Set(['product_name', 'unit', ...Array.from(selectedColumns)]));
            const params = new URLSearchParams({
                date: selectedDate,
                bars: Array.from(selectedBars).join(','),
                columns: cols.join(','),
                guest_count: guestCount || '0',
            });
            const endpoint = format === 'excel' ? 'detailed-excel' : 'detailed-pdf';
            const res = await fetch(`${API_URL}/api/reports/export/${endpoint}?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const ext = format === 'excel' ? 'xlsx' : 'pdf';
            const a = document.createElement('a');
            a.href = url;
            a.download = `rapor_${selectedDate}.${ext}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success('Dosya indirildi');
        } catch (e) {
            toast.error('İndirme başarısız: ' + e.message);
        }
    };

    const handleDetailedExport = () => handleDetailedDownload('pdf');

    const handleConsolidatedExport = async () => {
        if (selectedBars.size === 0) {
            toast.error('En az bir bar secin');
            return;
        }
        try {
            toast.info('PDF hazırlanıyor...');
            const params = new URLSearchParams({
                date: selectedDate,
                bars: Array.from(selectedBars).join(','),
                guest_count: guestCount || 0,
            });
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/reports/export/consolidated-pdf?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `konsolide_rapor_${selectedDate}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success('PDF indirildi');
        } catch (e) {
            toast.error('PDF indirilemedi: ' + e.message);
        }
    };

    const handleDownloadPreviewedPdf = () => {
        if (!pdfPreviewUrl) return;
        // Use the download blob URL (octet-stream) which forces browser to download
        const downloadUrl = pdfDownloadUrl || pdfPreviewUrl;
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = pdfFilename;
        a.target = '_self';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => document.body.removeChild(a), 100);
        toast.success('Indirme baslatildi');
    };

    const closePdfPreview = () => {
        if (pdfPreviewUrl) window.URL.revokeObjectURL(pdfPreviewUrl);
        if (pdfDownloadUrl) window.URL.revokeObjectURL(pdfDownloadUrl);
        setPdfPreviewUrl(null);
        setPdfDownloadUrl(null);
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (!e.target.closest('[data-dropdown="bars"]')) setBarDropdownOpen(false);
            if (!e.target.closest('[data-dropdown="cols"]')) setColDropdownOpen(false);
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, []);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({ date: selectedDate, guest_count: guestCount || '0' });
            const res = await fetch(`${API_URL}/api/reports/daily?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setReportData(await res.json());
            } else {
                toast.error('Rapor alinamadi');
            }
        } catch (error) {
            toast.error('Rapor yuklenirken hata olustu');
        } finally {
            setLoading(false);
        }
    };

    const fetchTrend = async () => {
        try {
            setTrendLoading(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/reports/bar-trend?period=${trendPeriod}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) setTrendData(await res.json());
        } catch (error) {
            console.error('Trend error:', error);
        } finally {
            setTrendLoading(false);
        }
    };

    const fetchCategory = async () => {
        try {
            setCategoryLoading(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/reports/category-analysis?date=${selectedDate}&period=${categoryPeriod}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) setCategoryData(await res.json());
        } catch (error) {
            console.error('Category error:', error);
        } finally {
            setCategoryLoading(false);
        }
    };

    const fetchComparison = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/reports/comparison`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) setComparisonData(await res.json());
        } catch (error) {
            console.error('Comparison error:', error);
        }
    };

    const fetchTopProducts = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/reports/top-products?period=${topProductsPeriod}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) setTopProductsData(await res.json());
        } catch (error) {
            console.error('Top products error:', error);
        }
    };

    const fetchGuestTrend = async () => {
        try {
            setGuestTrendLoading(true);
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({ period: guestTrendPeriod });
            if (guestTrendEndDate) params.set('end_date', guestTrendEndDate);
            const res = await fetch(`${API_URL}/api/reports/guest-cost-trend?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) setGuestTrendData(await res.json());
        } catch (error) {
            console.error('Guest trend error:', error);
        } finally {
            setGuestTrendLoading(false);
        }
    };

    const handleExport = (format) => {
        // Redirect to detailed download
        handleDetailedDownload(format);
    };

    useEffect
    useEffect(() => { fetchTrend(); }, [trendPeriod]);
    useEffect(() => { fetchTopProducts(); }, [topProductsPeriod]);
    useEffect(() => { fetchGuestTrend(); }, [guestTrendPeriod, guestTrendEndDate]);

    // Mini bar chart
    const MiniBarChart = ({ data, color, maxVal }) => {
        const max = maxVal || Math.max(...data.map(d => d.total_tl), 1);
        return (
            <div className="flex items-end gap-px h-16">
                {data.map((item, idx) => (
                    <div key={idx} className="flex-1 relative group">
                        <div 
                            className="w-full rounded-t transition-all duration-300"
                            style={{ 
                                height: `${Math.max(2, (item.total_tl / max) * 100)}%`,
                                backgroundColor: item.total_tl > 0 ? color : '#e2e8f0',
                                minHeight: '2px'
                            }}
                        />
                        {item.total_tl > 0 && (
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10 pointer-events-none">
                                {item.label}: {item.total_tl.toFixed(0)} TL
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    // Category pie (CSS-based)
    const CategoryPieChart = ({ categories, total }) => {
        if (!categories || categories.length === 0 || total === 0) {
            return <div className="h-40 flex items-center justify-center text-slate-400 text-sm">Veri yok</div>;
        }
        let accDeg = 0;
        const gradParts = categories.map(c => {
            const deg = (c.percentage / 100) * 360;
            const part = `${c.color} ${accDeg}deg ${accDeg + deg}deg`;
            accDeg += deg;
            return part;
        });
        return (
            <div className="flex items-center gap-6">
                <div 
                    className="w-36 h-36 rounded-full flex-shrink-0"
                    style={{ background: `conic-gradient(${gradParts.join(', ')})` }}
                />
                <div className="space-y-2 flex-1">
                    {categories.map((cat) => (
                        <div key={cat.category} className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                            <span className="text-sm text-slate-700 font-medium">{cat.label}</span>
                            <span className="text-xs text-slate-400 ml-auto">{cat.percentage}%</span>
                            <span className="text-sm font-semibold text-slate-800 w-20 text-right">{cat.total_tl.toFixed(0)} TL</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6" data-testid="reports-page">
            <ReportCenter API_URL={API_URL} BAR_NAMES={BAR_NAMES} />

            {/* ===== BAR TREND SECTION removed per user request ===== */}

            {/* ===== GUEST PER-CAPITA COST TREND ===== */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <UserCheck className="w-5 h-5 text-orwion-green" />
                                Misafir Basi Maliyet Trendi
                            </CardTitle>
                            <CardDescription>
                                {guestTrendPeriod === 'weekly' ? 'Son 7 gunluk' : 'Son 30 gunluk'} kisi basi maliyet trendi
                                {guestTrendEndDate && ` · Bitiş: ${guestTrendEndDate}`}
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            {/* Bitiş tarihi seçici — geçmiş dönemleri görüntülemek için */}
                            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg pl-2 pr-1 py-0.5">
                                <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                                <input
                                    type="date"
                                    value={guestTrendEndDate}
                                    onChange={(e) => setGuestTrendEndDate(e.target.value)}
                                    max={new Date().toISOString().slice(0, 10)}
                                    className="h-7 text-xs bg-transparent focus:outline-none w-[130px]"
                                    data-testid="guest-trend-end-date"
                                    title="Trend'in son gününü seçin — geçmiş ay/gün görüntüleme"
                                />
                                {guestTrendEndDate && (
                                    <button
                                        type="button"
                                        onClick={() => setGuestTrendEndDate('')}
                                        className="text-slate-400 hover:text-slate-700 text-xs px-1"
                                        title="Bugüne dön"
                                        data-testid="guest-trend-reset-date"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                            <div className="flex bg-slate-100 rounded-lg p-0.5">
                                <Button
                                    variant={guestTrendPeriod === 'weekly' ? 'default' : 'ghost'}
                                    size="sm"
                                    className={guestTrendPeriod === 'weekly' ? 'h-7 text-xs' : 'h-7 text-xs text-slate-500'}
                                    onClick={() => setGuestTrendPeriod('weekly')}
                                    data-testid="guest-trend-weekly-btn"
                                >
                                    <CalendarDays className="w-3.5 h-3.5 mr-1" />Haftalik
                                </Button>
                                <Button
                                    variant={guestTrendPeriod === 'monthly' ? 'default' : 'ghost'}
                                    size="sm"
                                    className={guestTrendPeriod === 'monthly' ? 'h-7 text-xs' : 'h-7 text-xs text-slate-500'}
                                    onClick={() => setGuestTrendPeriod('monthly')}
                                    data-testid="guest-trend-monthly-btn"
                                >
                                    <CalendarRange className="w-3.5 h-3.5 mr-1" />Aylik
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {guestTrendLoading ? (
                        <Skeleton className="h-48" />
                    ) : guestTrendData ? (
                        <div className="space-y-4">
                            {/* Average summary */}
                            <div className="flex flex-wrap gap-4">
                                <div className="bg-indigo-50 rounded-lg p-4 flex-1 min-w-[200px]">
                                    <div className="text-xs text-indigo-600 mb-1">Ortalama Kisi Basi Maliyet</div>
                                    <p className="text-2xl font-bold text-indigo-800">
                                        {guestTrendData.avg_per_person_tl?.toFixed(2) || '0.00'} TL
                                    </p>
                                    <p className="text-xs text-indigo-500 mt-1">
                                        {guestTrendData.days_with_data || 0} gun verisi mevcut
                                    </p>
                                </div>
                            </div>

                            {/* Trend Chart - Bar chart */}
                            {(() => {
                                const daysWithValues = guestTrendData.trend.filter(t => t.per_person_tl > 0);
                                if (daysWithValues.length === 0) {
                                    return (
                                        <div className="text-center text-slate-400 py-8">
                                            <UserCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                            <p>Misafir sayisi girilen gun bulunamadi</p>
                                            <p className="text-xs mt-1">Bar stok sayfalarindan gunluk misafir sayisini girin</p>
                                        </div>
                                    );
                                }

                                const maxVal = Math.max(...guestTrendData.trend.map(t => t.per_person_tl), 1);
                                const maxTotalTL = Math.max(...guestTrendData.trend.map(t => t.total_tl), 1);

                                return (
                                    <div className="space-y-6" data-testid="guest-trend-chart-container">
                                        {/* Recharts Composed: Bar (TL) + Line (per-person) */}
                                        <div className="w-full h-72 bg-gradient-to-br from-indigo-50/40 to-white rounded-lg p-3 border border-indigo-100">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <ComposedChart data={guestTrendData.trend} margin={{ top: 16, right: 24, left: 0, bottom: 0 }}>
                                                    <defs>
                                                        <linearGradient id="colTotal" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.85}/>
                                                            <stop offset="100%" stopColor="#10b981" stopOpacity={0.45}/>
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false}/>
                                                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }}/>
                                                    <YAxis yAxisId="left" tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }}/>
                                                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#6366f1' }} axisLine={{ stroke: '#c7d2fe' }}/>
                                                    <Tooltip
                                                        contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 8, color: '#f1f5f9', fontSize: 12 }}
                                                        formatter={(v, name) => {
                                                            if (name === 'Toplam (TL)') return [`${Number(v).toLocaleString('tr-TR')} TL`, name];
                                                            if (name === 'Kişi Başı (TL)') return [`${Number(v).toFixed(2)} TL/kişi`, name];
                                                            if (name === 'Misafir') return [`${Number(v).toLocaleString('tr-TR')} kişi`, name];
                                                            return [v, name];
                                                        }}
                                                    />
                                                    <Legend wrapperStyle={{ fontSize: 12 }}/>
                                                    <Bar yAxisId="left" dataKey="total_tl" name="Toplam (TL)" fill="url(#colTotal)" radius={[6,6,0,0]}/>
                                                    <Line yAxisId="right" type="monotone" dataKey="per_person_tl" name="Kişi Başı (TL)" stroke="#6366f1" strokeWidth={3} dot={{ r:4, fill:'#6366f1' }} activeDot={{ r:6 }}/>
                                                    <Line yAxisId="right" type="monotone" dataKey="guest_count" name="Misafir" stroke="#f59e0b" strokeDasharray="4 4" strokeWidth={2} dot={false}/>
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        </div>

                                        {/* Day-by-day detail table */}
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="border-b bg-slate-50">
                                                        <th className="text-left p-2 font-medium text-slate-600">Tarih</th>
                                                        <th className="text-right p-2 font-medium text-slate-600">Misafir</th>
                                                        <th className="text-right p-2 font-medium text-slate-600">Toplam (TL)</th>
                                                        <th className="text-right p-2 font-medium text-slate-600">Kisi Basi (TL)</th>
                                                        <th className="text-right p-2 font-medium text-slate-600">Kisi Basi (EUR)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {guestTrendData.trend.filter(t => t.total_tl > 0 || t.guest_count > 0).map((t) => (
                                                        <tr key={t.date} className="border-b hover:bg-slate-50">
                                                            <td className="p-2 text-slate-700">{t.label}</td>
                                                            <td className="p-2 text-right">
                                                                {t.guest_count > 0 ? (
                                                                    <Badge variant="outline" className="text-xs">{t.guest_count}</Badge>
                                                                ) : (
                                                                    <span className="text-slate-300">-</span>
                                                                )}
                                                            </td>
                                                            <td className="p-2 text-right font-medium">{t.total_tl.toFixed(0)} TL</td>
                                                            <td className="p-2 text-right font-bold text-indigo-700">
                                                                {t.per_person_tl > 0 ? `${t.per_person_tl.toFixed(2)} TL` : '-'}
                                                            </td>
                                                            <td className="p-2 text-right text-blue-600">
                                                                {t.per_person_eur > 0 ? `${t.per_person_eur.toFixed(2)} EUR` : '-'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    ) : (
                        <div className="text-center text-slate-400 py-8">
                            <UserCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>Misafir basi maliyet verisi yukleniyor...</p>
                        </div>
                    )}
                </CardContent>
            </Card>
            {/* ===== TOP PRODUCTS SECTION ===== */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-orwion-green" />
                                En Cok Tuketilen Urunler
                            </CardTitle>
                            <CardDescription>Urun bazli tuketim trendi</CardDescription>
                        </div>
                        <div className="flex bg-slate-100 rounded-lg p-0.5">
                            <Button
                                variant={topProductsPeriod === 'weekly' ? 'default' : 'ghost'}
                                size="sm"
                                className={topProductsPeriod === 'weekly' ? 'h-7 text-xs' : 'h-7 text-xs text-slate-500'}
                                onClick={() => setTopProductsPeriod('weekly')}
                                data-testid="top-products-weekly-btn"
                            >
                                Haftalik
                            </Button>
                            <Button
                                variant={topProductsPeriod === 'monthly' ? 'default' : 'ghost'}
                                size="sm"
                                className={topProductsPeriod === 'monthly' ? 'h-7 text-xs' : 'h-7 text-xs text-slate-500'}
                                onClick={() => setTopProductsPeriod('monthly')}
                                data-testid="top-products-monthly-btn"
                            >
                                Aylik
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {topProductsData && topProductsData.products?.length > 0 ? (
                        <div className="space-y-3">
                            {topProductsData.products.map((product, idx) => {
                                const maxConsumption = topProductsData.products[0]?.total_consumption || 1;
                                const barWidth = (product.total_consumption / maxConsumption) * 100;
                                const trendMax = Math.max(...product.trend.map(t => t.consumption), 1);
                                return (
                                    <div key={product.name} className="border rounded-lg p-3">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-3">
                                                <span className="w-6 h-6 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">{idx + 1}</span>
                                                <span className="font-medium text-slate-800">{product.name}</span>
                                            </div>
                                            <div className="flex items-center gap-4 text-sm">
                                                <span className="text-slate-500">{product.total_consumption} adet</span>
                                                <span className="font-semibold text-slate-800">{product.total_tl.toLocaleString('tr-TR')} TL</span>
                                            </div>
                                        </div>
                                        {/* Bar + mini trend */}
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 bg-slate-100 rounded-full h-2">
                                                <div className="bg-orwion-green h-2 rounded-full transition-all" style={{ width: `${barWidth}%` }} />
                                            </div>
                                            <div className="flex items-end gap-px h-6 w-24 flex-shrink-0">
                                                {product.trend.map((t, ti) => (
                                                    <div key={ti} className="flex-1 bg-slate-300 rounded-t" 
                                                         style={{ height: `${Math.max(2, (t.consumption / trendMax) * 100)}%`, backgroundColor: t.consumption > 0 ? '#22c55e' : '#e2e8f0', minHeight: '2px' }} />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center text-slate-400 py-8">
                            <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>Secili donemde urun verisi bulunamadi</p>
                        </div>
                    )}
                </CardContent>
            </Card>

        </div>
    );
}

export default Reports;

// ===================== GUEST COUNT BADGE (tarih bazlı) =====================
function GuestCountBadge({ dateFrom, dateTo }) {
    const [info, setInfo] = useState(null);
    useEffect(() => {
        if (!dateFrom) return;
        let cancelled = false;
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ date_from: dateFrom });
        if (dateTo && dateTo !== dateFrom) params.set('date_to', dateTo);
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/guest-count?${params}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (!cancelled && d) setInfo(d); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [dateFrom, dateTo]);
    if (!info) return null;
    return (
        <div className="mt-3 inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-1.5 text-sm" data-testid="rc-guest-count-badge">
            <span className="text-indigo-700 font-semibold">
                {info.kind === 'single' ? 'Misafir Sayısı:' : `Toplam Misafir (${info.days} gün):`}
            </span>
            <span className="text-indigo-900 font-bold text-base">
                {info.total_guests > 0 ? info.total_guests.toLocaleString('tr-TR') : '-'}
            </span>
            <span className="text-[11px] text-slate-500">otomatik</span>
        </div>
    );
}

// ===================== REPORT CENTER (new professional builder) =====================
function ReportCenter({ API_URL, BAR_NAMES }) {
    const today = new Date().toISOString().slice(0, 10);
    const monthAgo = (() => { const d = new Date(); d.setDate(d.getDate() - 29); return d.toISOString().slice(0, 10); })();
    const [reportKind, setReportKind] = useState('bar');  // bar | complimentary | treats | consumables
    const [dateFrom, setDateFrom] = useState(monthAgo);
    const [dateTo, setDateTo] = useState(today);
    const [format, setFormat] = useState('consolidated');
    const [guests, setGuests] = useState('');
    const [includeConsumables, setIncludeConsumables] = useState(true);
    // Bar selector: multi-select for bar/consumables, single-select for treats, hidden for complimentary
    const [selectedBars, setSelectedBars] = useState(new Set(Object.keys(BAR_NAMES)));
    const [singleBar, setSingleBar] = useState('');
    const [barDropdownOpen, setBarDropdownOpen] = useState(false);
    const [emailDialog, setEmailDialog] = useState(false);
    const [emailTo, setEmailTo] = useState('');
    const [emailing, setEmailing] = useState(false);
    const [manualOpen, setManualOpen] = useState(false);

    // Reset format when changing report kind
    useEffect(() => {
        if (reportKind === 'bar') setFormat('consolidated');
        else if (reportKind === 'consumables') setFormat('detail');
        else setFormat('detail');
    }, [reportKind]);

    // Close bar dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (!e.target.closest('[data-dropdown="rc-bars"]')) setBarDropdownOpen(false);
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, []);

    const toggleBar = (id) => {
        setSelectedBars(prev => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };
    const toggleAllBars = () => {
        if (selectedBars.size === Object.keys(BAR_NAMES).length) setSelectedBars(new Set());
        else setSelectedBars(new Set(Object.keys(BAR_NAMES)));
    };

    const buildParams = () => {
        const p = new URLSearchParams();
        if (reportKind === 'bar') {
            // Bar uses single "date" (Bitiş kabul edilir)
            p.set('date', dateTo);
            // Range tarihi gönder ki backend [date_from..date_to] toplam misafir sayısını hesaplasın
            if (dateFrom && dateFrom !== dateTo) p.set('date_from', dateFrom);
            if (guests) p.set('guest_count', guests);
            if (selectedBars.size > 0 && selectedBars.size < Object.keys(BAR_NAMES).length) {
                p.set('bars', Array.from(selectedBars).join(','));
            }
            if (format === 'consolidated' && includeConsumables) p.set('include_consumables', 'true');
        } else if (reportKind === 'consumables') {
            p.set('date_from', dateFrom);
            p.set('date_to', dateTo);
            if (singleBar) p.set('bar_id', singleBar);
        } else {
            // complimentary or treats
            p.set('date_from', dateFrom);
            p.set('date_to', dateTo);
            p.set('report_type', format);
            if (reportKind === 'treats' && singleBar) p.set('bar_id', singleBar);
        }
        return p;
    };

    const getEndpoint = () => {
        if (reportKind === 'bar') {
            return format === 'excel'
                ? '/api/reports/export/detailed-excel'
                : format === 'detailed'
                    ? '/api/reports/export/detailed-pdf'
                    : '/api/reports/export/consolidated-pdf';
        }
        if (reportKind === 'consumables') return '/api/consumables/export/pdf';
        return reportKind === 'complimentary' ? '/api/complimentary/export/pdf' : '/api/treats/export/pdf';
    };

    const download = () => {
        const p = buildParams();
        const token = localStorage.getItem('token');
        if (token) p.set('token', token);
        window.open(`${API_URL}${getEndpoint()}?${p.toString()}`, '_blank');
    };

    const sendEmail = async () => {
        if (!emailTo.trim()) { toast.error('E-posta adresi girin'); return; }
        setEmailing(true);
        try {
            const token = localStorage.getItem('token');
            const body = {
                to: emailTo.trim(),
                report_kind: reportKind,
                format,
                date_from: dateFrom,
                date_to: dateTo,
                bar_id: (reportKind === 'treats' || reportKind === 'consumables') ? (singleBar || null) : null,
                guest_count: guests ? parseInt(guests, 10) : null,
                include_consumables: reportKind === 'bar' && format === 'consolidated' ? includeConsumables : false,
            };
            const res = await fetch(`${API_URL}/api/reports/email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Gönderim başarısız');
            }
            toast.success('Rapor e-posta ile gönderildi');
            setEmailDialog(false);
            setEmailTo('');
        } catch (e) {
            toast.error(e.message);
        } finally {
            setEmailing(false);
        }
    };

    const kindMeta = {
        bar: { label: 'Bar Stok Raporu', desc: 'Günlük tüm barlarda satış / stok takibi', activeColor: 'bg-blue-600 text-white border-blue-600', color: 'bg-blue-50 border-blue-200 text-blue-700' },
        complimentary: { label: 'Ödenmez Raporu', desc: 'İdari personel ödenmez kayıtları (EUR)', activeColor: 'bg-amber-600 text-white border-amber-600', color: 'bg-amber-50 border-amber-200 text-amber-700' },
        treats: { label: 'İkram Raporu', desc: 'Misafir ikram (oda bazlı) kayıtları (EUR)', activeColor: 'bg-emerald-600 text-white border-emerald-600', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
        consumables: { label: 'Sarf Malzemeler Raporu', desc: 'Operasyonel sarf malzeme tüketimi', activeColor: 'bg-slate-700 text-white border-slate-700', color: 'bg-slate-50 border-slate-200 text-slate-700' },
    };

    const formatOptions = reportKind === 'bar'
        ? [
            { value: 'consolidated', label: 'Konsolide PDF' },
            { value: 'detailed', label: 'Detaylı PDF' },
            { value: 'excel', label: 'Excel (xlsx)' },
        ]
        : reportKind === 'consumables'
            ? [{ value: 'detail', label: 'Detaylı PDF' }]
            : [
                { value: 'detail', label: 'Detaylı PDF' },
                { value: 'summary', label: 'Sadece Toplam (€) PDF' },
            ];

    // Bar selector visibility & mode
    const showMultiBar = reportKind === 'bar';
    const showSingleBar = reportKind === 'treats' || reportKind === 'consumables';

    return (
        <>
        <Card className="shadow-lg border-slate-200" data-testid="report-center">
            <CardHeader className="border-b bg-gradient-to-r from-slate-50 to-white">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <CardTitle className="text-xl">Rapor Merkezi</CardTitle>
                            <CardDescription>Rapor türünü seçin, parametreleri belirleyin, indirin veya e-posta ile gönderin.</CardDescription>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={async () => {
                                const df = window.prompt('TCMB kur backfill — Başlangıç (YYYY-MM-DD)', new Date(Date.now() - 30*86400000).toISOString().slice(0,10));
                                if (!df) return;
                                const dt = window.prompt('Bitiş (YYYY-MM-DD)', new Date().toISOString().slice(0,10));
                                if (!dt) return;
                                try {
                                    const token = localStorage.getItem('token');
                                    const res = await fetch(`${API_URL}/api/tcmb-rate/backfill`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                        body: JSON.stringify({ date_from: df, date_to: dt })
                                    });
                                    const j = await res.json();
                                    if (!res.ok) { alert('Hata: ' + (j.detail || res.statusText)); return; }
                                    alert(`TCMB Kur Backfill Tamamlandı\n\n${df} → ${dt}\nToplam gün: ${j.total_days}\nÇekilen: ${j.fetched}\nMevcut (atlandı): ${j.skipped_existing}\nHafta sonu/tatil: ${j.weekend_or_holiday}\nHata: ${j.failure_count}`);
                                } catch (e) { alert('Bağlantı hatası: ' + e.message); }
                            }}
                            variant="outline"
                            size="sm"
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                            data-testid="tcmb-backfill-btn"
                            title="TCMB'den eksik günlük kurları çeker (ortalama hesabında hassasiyeti artırır)"
                        >
                            🔄 Kur Backfill
                        </Button>
                        <Button
                            onClick={() => setManualOpen(true)}
                            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-sm"
                            data-testid="open-manual-report-btn"
                        >
                            <FileText className="w-4 h-4 mr-2" /> Manuel Rapor
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
                {/* STEP 1: Report Type */}
                <div>
                    <Label className="text-xs font-semibold text-slate-700 mb-2 block">1. Rapor Türü</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(kindMeta).map(([k, m]) => (
                            <button
                                key={k}
                                type="button"
                                onClick={() => setReportKind(k)}
                                className={`text-left p-3 rounded-xl border-2 transition-all hover:scale-[1.01] ${reportKind === k ? m.activeColor : m.color}`}
                                data-testid={`report-kind-${k}`}
                            >
                                <div className="font-semibold text-sm">{m.label}</div>
                                <div className="text-[11px] opacity-80 mt-0.5 leading-tight">{m.desc}</div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* STEP 2: Parameters - Bar | Guest | Date Range | Format */}
                <div>
                    <Label className="text-xs font-semibold text-slate-700 mb-2 block">2. Parametreler</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                        {/* Bar selector (multi for bar, single for treats/consumables) */}
                        {(showMultiBar || showSingleBar) && (
                            <div className="relative" data-dropdown="rc-bars">
                                <Label className="text-xs text-slate-500">Bar Seç</Label>
                                {showMultiBar ? (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setBarDropdownOpen(o => !o)}
                                            className="w-full h-10 flex items-center justify-between px-3 bg-white border border-slate-200 rounded-md hover:border-slate-400 transition-all text-sm"
                                            data-testid="rc-bars-btn"
                                        >
                                            <span className="text-slate-700 truncate">
                                                {selectedBars.size === 0 ? 'Seç...' :
                                                 selectedBars.size === Object.keys(BAR_NAMES).length ? 'Tüm barlar' :
                                                 `${selectedBars.size} bar`}
                                            </span>
                                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${barDropdownOpen ? 'rotate-180' : ''}`} />
                                        </button>
                                        {barDropdownOpen && (
                                            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-72 overflow-auto min-w-[180px]">
                                                <button type="button" onClick={toggleAllBars}
                                                    className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 text-sm font-medium text-slate-700 flex items-center gap-2">
                                                    <input type="checkbox" checked={selectedBars.size === Object.keys(BAR_NAMES).length} readOnly className="w-4 h-4 pointer-events-none" />
                                                    Tümünü Seç / Bırak
                                                </button>
                                                {Object.entries(BAR_NAMES).map(([barId, name]) => (
                                                    <button key={barId} type="button" onClick={() => toggleBar(barId)}
                                                        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm text-slate-700"
                                                        data-testid={`rc-bar-${barId}`}>
                                                        <input type="checkbox" checked={selectedBars.has(barId)} readOnly className="w-4 h-4 pointer-events-none" />
                                                        {name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <select value={singleBar} onChange={(e) => setSingleBar(e.target.value)}
                                        className="h-10 w-full border border-slate-200 rounded-md px-3 text-sm bg-white"
                                        data-testid="rc-single-bar">
                                        <option value="">Tüm Barlar</option>
                                        {Object.entries(BAR_NAMES).map(([k, n]) => <option key={k} value={k}>{n}</option>)}
                                    </select>
                                )}
                            </div>
                        )}

                        {/* Guest Count (only bar) */}
                        {reportKind === 'bar' && (
                            <div>
                                <Label className="text-xs text-slate-500">Misafir Sayısı</Label>
                                <Input type="number" value={guests} onChange={(e) => setGuests(e.target.value)}
                                    placeholder="opsiyonel" className="h-10" data-testid="rc-guests" />
                            </div>
                        )}

                        {/* Date Range - From */}
                        <div>
                            <Label className="text-xs text-slate-500">Başlangıç Tarihi</Label>
                            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                                className="h-10" data-testid="rc-date-from" />
                        </div>
                        {/* Date Range - To */}
                        <div>
                            <Label className="text-xs text-slate-500">Bitiş Tarihi</Label>
                            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                className="h-10" data-testid="rc-date-to" />
                        </div>

                        {/* Format Dropdown */}
                        <div>
                            <Label className="text-xs text-slate-500">Format</Label>
                            <select value={format} onChange={(e) => setFormat(e.target.value)}
                                className="h-10 w-full border border-slate-200 rounded-md px-3 text-sm bg-white"
                                data-testid="rc-format">
                                {formatOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Include consumables checkbox (only bar+consolidated) */}
                    {reportKind === 'bar' && format === 'consolidated' && (
                        <label className="flex items-center gap-2 mt-3 text-sm text-slate-700 cursor-pointer select-none" data-testid="rc-include-consumables-wrap">
                            <input
                                type="checkbox"
                                checked={includeConsumables}
                                onChange={(e) => setIncludeConsumables(e.target.checked)}
                                className="w-4 h-4 accent-blue-600"
                                data-testid="rc-include-consumables"
                            />
                            <span>Sarf Malzemeler bölümünü PDF sonuna ekle</span>
                        </label>
                    )}

                    {reportKind === 'bar' && (
                        <p className="text-[11px] text-slate-400 mt-2">Not: Bar raporlarında "Bitiş" tarihi referans alınır (günlük rapor).</p>
                    )}

                    <GuestCountBadge dateFrom={dateFrom} dateTo={reportKind === 'bar' ? dateTo : dateTo} />
                </div>

                {/* STEP 3: Actions */}
                <div className="pt-3 border-t flex flex-wrap gap-2 justify-end">
                    <Button variant="outline" onClick={() => setEmailDialog(true)} data-testid="email-report-btn">
                        <Calendar className="w-4 h-4 mr-2" /> E-posta ile Gönder
                    </Button>
                    <Button onClick={download} className="bg-blue-600 hover:bg-blue-700" data-testid="dl-report-btn">
                        <Download className="w-4 h-4 mr-2" /> İndir
                    </Button>
                </div>
            </CardContent>
        </Card>

        {/* Email Dialog */}
        {emailDialog && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setEmailDialog(false)}>
                <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                    <h3 className="text-lg font-bold">Rapor E-posta Gönder</h3>
                    <p className="text-sm text-slate-500">
                        <strong>{kindMeta[reportKind].label}</strong> · {formatOptions.find(o => o.value === format)?.label}
                        <br />Dönem: {dateFrom} → {dateTo}
                    </p>
                    <div>
                        <Label className="text-xs">Alıcı E-posta(lar) - virgül ile ayırın</Label>
                        <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="ornek@grandkaptan.com" data-testid="email-to-input" />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setEmailDialog(false)}>İptal</Button>
                        <Button onClick={sendEmail} disabled={emailing} className="bg-blue-600 hover:bg-blue-700" data-testid="email-send-btn">
                            {emailing ? 'Gönderiliyor...' : 'Gönder'}
                        </Button>
                    </div>
                </div>
            </div>
        )}
        {/* Manuel Rapor Modal */}
        <ManualReportModal open={manualOpen} onClose={() => setManualOpen(false)} />
        </>
    );
}
